import React, { useState, useEffect, useCallback, useRef } from 'react';
import LookCanvas from './LookCanvas';
import LookTab from './LookTab';
import LayersTab from './LayersTab';
import TextTab from './TextTab';
import GuidesTab from './GuidesTab';
import LayerPanel, { syncLayerOrder } from './LayerPanel';
import useUndoRedo from './useUndoRedo';
import { createLook, updateLook } from '../shared/api';
import { invalidateThumbnails } from '../shared/thumbnailCache';
import { InlineSpinner } from '../shared/Spinner';

const PANEL_TABS = ['Look', 'Guides', 'Layers', 'Text'];

const SAMPLE_VARIABLES = {
  first_name: 'Emily',
  last_name: 'Johnson',
  grade: '5th',
  school_name: 'Lincoln Elementary',
};

const defaultLook = {
  name: '',
  season: '',
  pose_number: '-01',
  look_type: 'single',
  canvas_width: 2400,
  canvas_height: 3000,
  canvas_dpi: 300,
  eyes_y: null,
  face_height: null,
  center_x: null,
  rotation: 0,
  source_if_3: null,
  source_if_2: null,
  canvas_filepath: null,
  canvas_filename: null,
  canvas_bg_ref_data: null,
  collage_photos: [
    { look_ref: null, season: null, x: null, y: null, width: null, height: null },
    { look_ref: null, season: null, x: null, y: null, width: null, height: null }
  ],
  overlay_s3_path: null,
  overlay_filename: null,
  mascot_center_x: null,
  mascot_center_y: null,
  mascot_size: null,
  layer_order: [],
  ref_photo_data: null,
  ref_zoom: 1,
  ref_rotation: 0,
  ref_offset_x: 0,
  ref_offset_y: 0,
  text_fields: []
};

const LookEditor = ({ look, looks: allLooks, referencePoses, onSaved, onBack, onDirtyChange }) => {
  const [activePanel, setActivePanel] = useState('Look');
  const {
    value: formData, setValue: setFormData,
    previewValue, commitPreview,
    undo, redo, canUndo, canRedo, reset
  } = useUndoRedo(look || defaultLook);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedElement, setSelectedElement] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [sampleData, setSampleData] = useState({ ...SAMPLE_VARIABLES });
  const removedFieldIds = useRef([]);

  useEffect(() => {
    reset(look || defaultLook);
    setDirty(false);
    setError(null);
    setValidationErrors({});
    removedFieldIds.current = [];
  }, [look]);

  // Report dirty state to parent
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (!isMeta) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  // Delete/Backspace to remove selected collage photo
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      // Don't trigger when typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (!selectedElement || selectedElement.type !== 'collage_photo') return;
      if (formData.look_type !== 'collage') return;

      e.preventDefault();
      const idx = selectedElement.index;
      const photos = formData.collage_photos || [];
      if (idx < 0 || idx >= photos.length) return;

      setFormData(prev => ({
        ...prev,
        collage_photos: prev.collage_photos.filter((_, i) => i !== idx),
        _selectedCollagePhoto: null
      }));
      setSelectedElement(null);
      setDirty(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedElement, formData.look_type, formData.collage_photos]);

  const update = (changes) => {
    setFormData(prev => ({ ...prev, ...changes }));
    setDirty(true);
  };

  const updateTextField = (index, changes) => {
    setFormData(prev => {
      const fields = [...(prev.text_fields || [])];
      fields[index] = { ...fields[index], ...changes };
      return { ...prev, text_fields: fields };
    });
    setDirty(true);
  };

  const addTextField = () => {
    setFormData(prev => ({
      ...prev,
      text_fields: [...(prev.text_fields || []), {
        position: (prev.text_fields || []).length,
        content: '{{first_name}}',
        font_s3_path: 's3://shoobphoto/fonts/',
        font_filename: 'ADAM-CG-PRO.REGULAR.OTF',
        font_color: '#000000',
        font_size_min: 80,
        font_size_max: 160,
        center_x: 0.5,
        center_y: 0.85,
        bound_x_left: 0.04,
        bound_x_right: 0.96,
        sizing_rule: 'shrink_to_fit',
        align: 'center',
        bold: false,
        italic: false,
        shadow: false
      }]
    }));
    setDirty(true);
  };

  const removeTextField = (index) => {
    setFormData(prev => {
      const removed = prev.text_fields[index];
      if (removed?.id) removedFieldIds.current.push(removed.id);
      return {
        ...prev,
        text_fields: prev.text_fields.filter((_, i) => i !== index)
          .map((tf, i) => ({ ...tf, position: i }))
      };
    });
    setDirty(true);
  };

  // Canvas drag: live preview without history push
  const handleCanvasChange = useCallback((newFormData) => {
    previewValue(newFormData);
    setDirty(true);
  }, [previewValue]);

  // Canvas drag end: commit to undo history
  const handleCanvasCommit = useCallback(() => {
    commitPreview();
  }, [commitPreview]);

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    onBack();
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.name) errs.name = 'Required';
    if (!formData.season) errs.season = 'Required';
    if (!formData.canvas_width) errs.canvas_width = 'Required';
    if (!formData.canvas_height) errs.canvas_height = 'Required';
    if (!formData.canvas_dpi) errs.canvas_dpi = 'Required';

    // Face/eye data is only required for singles and collages that don't use pre-existing looks
    // A "look-only collage" has all its photos referencing existing looks — no direct face placement needed
    const isLookOnlyCollage = formData.look_type === 'collage' &&
      (formData.collage_photos || []).length > 0 &&
      (formData.collage_photos || []).every(p => p.look_ref);

    if (!isLookOnlyCollage) {
      if (formData.eyes_y == null) errs.eyes_y = 'Required';
      if (formData.face_height == null) errs.face_height = 'Required';
      if (formData.center_x == null) errs.center_x = 'Required';
    }

    if (formData.look_type === 'single') {
      if (formData.source_if_3 == null && formData.source_if_2 == null) {
        errs.source_if_3 = 'At least one source required';
        errs.source_if_2 = 'At least one source required';
      }
    }

    // Unique name check
    if (formData.name && allLooks) {
      const dup = allLooks.find(l => l.name === formData.name && l.id !== formData.id);
      if (dup) errs.name = 'This name is already in use';
    }

    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    setValidationErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Please fix the highlighted fields before saving.');
      // Navigate to the tab containing the first error
      const guidesFields = ['eyes_y', 'face_height', 'center_x'];
      const hasGuidesError = guidesFields.some(f => errs[f]);
      const lookFields = ['name', 'season', 'canvas_width', 'canvas_height', 'canvas_dpi', 'source_if_3', 'source_if_2'];
      const hasLookError = lookFields.some(f => errs[f]);
      if (hasLookError) setActivePanel('Look');
      else if (hasGuidesError) setActivePanel('Guides');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const textFieldAttrs = (formData.text_fields || []).map((tf, i) => ({
        id: tf.id || undefined,
        position: i,
        content: tf.content,
        font_s3_path: tf.font_s3_path,
        font_filename: tf.font_filename,
        font_color: tf.font_color,
        font_size_min: tf.font_size_min,
        font_size_max: tf.font_size_max,
        center_x: tf.center_x,
        center_y: tf.center_y,
        bound_x_left: tf.bound_x_left,
        bound_x_right: tf.bound_x_right,
        sizing_rule: tf.sizing_rule,
        align: tf.align,
        bold: tf.bold,
        italic: tf.italic,
        shadow: tf.shadow
      }));

      // Include _destroy entries for removed text fields
      removedFieldIds.current.forEach(id => {
        textFieldAttrs.push({ id, _destroy: true });
      });

      const payload = {
        ...formData,
        look_text_fields_attributes: textFieldAttrs
      };

      // Remove keys that shouldn't be sent to the API
      delete payload.text_fields;
      delete payload.created_at;
      delete payload.updated_at;
      // ref_photo_data IS sent so it persists in the database
      delete payload.ref_zoom;
      delete payload.ref_rotation;
      delete payload.ref_offset_x;
      delete payload.ref_offset_y;
      delete payload._selectedCollagePhoto;

      const isNew = !formData.id;
      let saved;
      if (formData.id) {
        saved = await updateLook(formData.id, payload);
      } else {
        saved = await createLook(payload);
      }

      removedFieldIds.current = [];
      if (saved.id) invalidateThumbnails(saved.id);
      reset(saved);
      setDirty(false);
      setValidationErrors({});
      onSaved(saved, isNew);
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || err.message);
    }
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Left panel */}
      <div style={{
        width: 340, borderRight: '1px solid #e0e0e0', display: 'flex',
        flexDirection: 'column', background: '#fafafa', flexShrink: 0
      }}>
        {/* Panel tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
          {PANEL_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActivePanel(tab)}
              style={{
                flex: 1, padding: '8px 4px', border: 'none',
                borderBottom: activePanel === tab ? '2px solid #1976d2' : '2px solid transparent',
                background: 'none', fontSize: 12, fontWeight: activePanel === tab ? 600 : 400,
                color: activePanel === tab ? '#1976d2' : '#888', cursor: 'pointer'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Panel content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {activePanel === 'Look' && (
            <LookTab
              formData={formData}
              onChange={update}
              validationErrors={validationErrors}
              allLooks={allLooks}
              referencePoses={referencePoses}
            />
          )}
          {activePanel === 'Layers' && (
            <>
              <LayersTab formData={formData} onChange={update} />
              <div style={{ borderTop: '1px solid #e0e0e0', marginTop: 16, paddingTop: 16 }}>
                <LayerPanel
                  formData={formData}
                  onChange={update}
                  selectedElement={selectedElement}
                />
              </div>
            </>
          )}
          {activePanel === 'Text' && (
            <TextTab
              textFields={formData.text_fields || []}
              onUpdate={updateTextField}
              onAdd={addTextField}
              onRemove={removeTextField}
            />
          )}
          {activePanel === 'Guides' && (
            <GuidesTab formData={formData} onChange={update} validationErrors={validationErrors} />
          )}
        </div>

        {/* Save bar with undo/redo */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid #e0e0e0',
          display: 'flex', gap: 8, alignItems: 'center'
        }}>
          <button
            onClick={handleBack}
            style={{
              padding: '8px 16px', background: '#eee', border: 'none',
              borderRadius: 4, cursor: 'pointer', fontSize: 13
            }}
          >
            Back
          </button>
          <button
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            style={{
              padding: '6px 10px', background: canUndo ? '#e3f2fd' : '#f5f5f5',
              border: '1px solid ' + (canUndo ? '#90caf9' : '#e0e0e0'),
              borderRadius: 4, cursor: canUndo ? 'pointer' : 'default',
              fontSize: 13, color: canUndo ? '#1565c0' : '#bbb', lineHeight: 1
            }}
          >
            ↶
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            style={{
              padding: '6px 10px', background: canRedo ? '#e3f2fd' : '#f5f5f5',
              border: '1px solid ' + (canRedo ? '#90caf9' : '#e0e0e0'),
              borderRadius: 4, cursor: canRedo ? 'pointer' : 'default',
              fontSize: 13, color: canRedo ? '#1565c0' : '#bbb', lineHeight: 1
            }}
          >
            ↷
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            style={{
              flex: 1, padding: '8px 16px',
              background: dirty ? '#1976d2' : '#bbb',
              color: '#fff', border: 'none', borderRadius: 4,
              cursor: dirty ? 'pointer' : 'default', fontSize: 13, fontWeight: 500
            }}
          >
            {saving ? <><InlineSpinner size={14} color="#fff" /> Saving...</> : formData.id ? 'Update Look' : 'Save New Look'}
          </button>
        </div>
        {error && (
          <div style={{ padding: '8px 16px', color: '#d32f2f', fontSize: 12 }}>{error}</div>
        )}
      </div>

      {/* Canvas area */}
      <div style={{ flex: 1, overflow: 'hidden', background: '#e8e8e8', display: 'flex', flexDirection: 'column' }}>
        {/* Simulation toolbar */}
        <div style={{
          padding: '8px 16px', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flexShrink: 0
        }}>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={simulating}
              onChange={e => setSimulating(e.target.checked)}
            />
            Simulate Student Data
          </label>
          {simulating && (
            <>
              {Object.keys(SAMPLE_VARIABLES).map(key => (
                <div key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <label style={{ fontSize: 10, color: '#666' }}>{key.replace(/_/g, ' ')}:</label>
                  <input
                    type="text"
                    value={sampleData[key] || ''}
                    onChange={e => setSampleData(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{
                      width: 80, padding: '2px 4px', border: '1px solid #ccc',
                      borderRadius: 3, fontSize: 11
                    }}
                  />
                </div>
              ))}
            </>
          )}
        </div>
        <LookCanvas
          formData={formData}
          allLooks={allLooks}
          onChange={handleCanvasChange}
          onCommit={handleCanvasCommit}
          simulationData={simulating ? sampleData : null}
          onElementSelect={setSelectedElement}
        />
      </div>
    </div>
  );
};

export default LookEditor;
